import { useState, type FormEvent } from 'react';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../auth-context';

type Category = 'getting-started' | 'standards' | 'patterns' | 'examples' | 'reference';

const CATEGORY_OPTIONS: { value: Category; label: string }[] = [
  { value: 'getting-started', label: 'Getting started' },
  { value: 'standards', label: 'QC standards' },
  { value: 'patterns', label: 'Patterns to spot' },
  { value: 'examples', label: 'Examples' },
  { value: 'reference', label: 'Reference' }
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function CreateTrainingDocModal({ open, onClose }: Props) {
  const { user, profile } = useAuth();
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<Category>('standards');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user || !profile) return;
    setError(null);
    setSubmitting(true);
    try {
      await addDoc(collection(db, 'training'), {
        title: title.trim(),
        category,
        body: body.trim(),
        createdBy: user.uid,
        createdByName: profile.displayName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      setTitle('');
      setCategory('standards');
      setBody('');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <h2 className="modal__title">New training doc</h2>
          <button className="modal__close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="modal__form" onSubmit={handleSubmit}>
          <label className="field">
            <span className="field__label">Title</span>
            <input
              type="text"
              required
              placeholder="e.g. How to QC a Push-to-View flow"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus
            />
          </label>

          <label className="field">
            <span className="field__label">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value as Category)}>
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Body</span>
            <textarea
              required
              rows={10}
              placeholder="Describe the standard, the pattern, the example. Plain text or markdown."
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </label>

          {error && <div className="form-error">{error}</div>}

          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn--primary" disabled={submitting || !title.trim() || !body.trim()}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
